import React, { useRef, useState, useEffect } from 'react';
import { useForm } from '@inertiajs/react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/alert';

interface WaitingListFormProps {
  successMessage?: string | null;
}

export function WaitingListForm({ successMessage = null }: WaitingListFormProps) {
  const [showSuccessMessage, setShowSuccessMessage] = useState(!!successMessage);
  const [formSuccessMessage, setFormSuccessMessage] = useState(successMessage || '');
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const formSectionRef = useRef<HTMLElement>(null);
  
  const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';

  const { data, setData, post, processing, errors, reset, transform } = useForm({
    first_name: '',
    email: '',
  });

  // Load reCAPTCHA script dynamically
  useEffect(() => {
    const loadRecaptcha = async () => {
      // Check if script already exists
      if (document.querySelector('script[src*="google.com/recaptcha"]')) {
        setRecaptchaLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => setRecaptchaLoaded(true);
      script.onerror = () => setRecaptchaLoaded(false);
      
      document.head.appendChild(script);
    };

    loadRecaptcha();
  }, [RECAPTCHA_SITE_KEY]);

  // Cleanup reCAPTCHA when component unmounts
  useEffect(() => {
    return () => {
      // Remove reCAPTCHA badge
      const recaptchaBadge = document.querySelector('.grecaptcha-badge');
      if (recaptchaBadge) {
        recaptchaBadge.remove();
      }
      
      // Clean up any reCAPTCHA elements
      const recaptchaElements = document.querySelectorAll('[id^="recaptcha"], [class*="recaptcha"]');
      recaptchaElements.forEach(element => {
        if (element.parentNode) {
          element.parentNode.removeChild(element);
        }
      });
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!recaptchaLoaded || !window.grecaptcha) {
      console.error('reCAPTCHA not loaded');
      return;
    }

    const recaptcha = window.grecaptcha;
    recaptcha.enterprise.ready(async () => {
      const token = await recaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, {action: 'WAITING_LIST_SIGNUP'});

      transform((data) => ({
        ...data,
        recaptchaToken: token,
      }));

      post(route('waiting-list.store'), {
        onSuccess: () => {
          setFormSuccessMessage('Thank you for joining our waiting list!');
          setShowSuccessMessage(true);
          reset();
          
          // Smooth scroll to the form section
          setTimeout(() => {
            formSectionRef.current?.scrollIntoView({ 
              behavior: 'smooth',
              block: 'center'
            });
          }, 100);
        }
      });
    });
  };

  return (
    <section id="waiting-list" className="py-6 md:py-10" ref={formSectionRef}>
      <div className="container mx-auto px-4">
        <div className="max-w-md mx-auto">
          <Card className="px-8 py-12 bg-white rounded-2xl gap-4 relative overflow-hidden">
            <div className="relative z-10">
              <div className="text-center mb-8">
                <h2 className="text-3xl mb-4 text-blue-800">Join our waiting list</h2>
                <p className="text-blue-800">
                  Gain exclusive early access to Protribe by joining our waiting list. We'll let you know when we're ready for you to join.
                </p>
              </div>

              {showSuccessMessage && (
                <Alert className="grid-cols-1 mb-6 bg-green-50 backdrop-blur-sm text-green-700 border border-green-200 p-4 rounded-lg">
                  {formSuccessMessage}
                </Alert>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="first_name" className="text-blue-800">First Name</Label>
                    <Input
                      id="first_name"
                      type="text"
                      value={data.first_name}
                      onChange={(e) => setData('first_name', e.target.value)}
                      className="w-full mt-1 bg-white backdrop-blur-sm border-blue-200 focus:border-indigo-500 focus:ring-indigo-500"
                      required
                    />
                    {errors.first_name && (
                      <p className="text-red-500 text-sm mt-1">{errors.first_name}</p>
                    )}
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="text-blue-800">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={data.email}
                      onChange={(e) => setData('email', e.target.value)}
                      className="w-full mt-1 bg-white backdrop-blur-sm border-blue-200 focus:border-indigo-500 focus:ring-indigo-500"
                      required
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                    )}
                  </div>
                  
                  <Button 
                    type="submit"
                    className="w-full bg-[#ffc20e] hover:bg-[#e5af0c] text-[#1b1b18] font-medium"
                    disabled={processing}
                  >
                    {processing ? 'Joining...' : 'Join Now'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}
